import React, { useRef, useState } from 'react'
import { Box } from '@mui/material'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCenter } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import { LayoutControls } from '../api/useDashboardLayout'
import { useCurrentRole } from '../currentRole'
import EmptyState from '../components/EmptyState'
import { TOUR_ANCHORS } from '../tour/anchors'
import DashboardGrid, { GridItem } from './DashboardGrid'
import DragHandle from './DragHandle'
import { LayoutDefinition, LayoutEntry } from './layout'
import PanelFrame from './PanelFrame'
import PanelMenu from './PanelMenu'
import { PanelConfigProvider } from './panelConfigContext'
import { PanelPatientProvider } from './panelContext'
import { PANELS, PanelId, PanelOfferScope, isPanelAccessibleForRole, isPanelOfferable } from './registry'
import SortableGridItem from './SortableGridItem'
import { useReorderDnd } from './useReorderDnd'

type Props = {
  uid: string
  layout: LayoutDefinition
  controls?: LayoutControls
  scope: PanelOfferScope
}

const DashboardRenderer = ({ uid, layout, controls, scope }: Props) => {
  const [role] = useCurrentRole()
  const ids = layout.panels.map((entry) => entry.panel)

  const [draggingId, setDraggingId] = useState<PanelId | null>(null)
  const gridRef = useRef<HTMLDivElement | null>(null)

  const draggingIndex = draggingId ? ids.indexOf(draggingId) : -1
  const draggingEntry = draggingIndex !== -1 ? { entry: layout.panels[draggingIndex], index: draggingIndex } : null

  const { sensors, onDragEnd: reorder } = useReorderDnd(ids, (from, to) => controls?.moveTo(from, to))

  const onDragStart = (event: DragStartEvent) => setDraggingId(event.active.id as PanelId)

  const onDragCancel = () => setDraggingId(null)

  const onDragEnd = (event: DragEndEvent) => {
    setDraggingId(null)
    reorder(event)
  }

  const renderPanel = (entry: LayoutEntry, index: number, dragHandle: React.ReactNode, editable: boolean) => {
    const panel = PANELS[entry.panel]
    // Fallback for a stored entry that predates the check, or a role switch or page move since it was
    // placed — the add paths (`AddComponentTile`, `DashboardCustomizeBar`, `PanelMenu`) gate this too.
    const offerable = isPanelOfferable(entry.panel, role, scope)

    return (
      <PanelConfigProvider
        config={entry.config}
        setConfig={editable && controls ? (config) => controls.configure(index, config) : undefined}
      >
        <PanelFrame
          title={panel.title}
          chrome={panel.chrome}
          dragHandle={dragHandle}
          menu={
            editable && controls ? (
              <PanelMenu layout={layout} index={index} controls={controls} scope={scope} />
            ) : undefined
          }
          collapsible={panel.collapsible}
          tourAnchor={TOUR_ANCHORS.panel(entry.panel)}
        >
          {offerable ? (
            <panel.Component />
          ) : (
            <EmptyState
              title="Not available"
              description={
                isPanelAccessibleForRole(entry.panel, role)
                  ? "This component isn't available on this page."
                  : "This component isn't available for your current role."
              }
            />
          )}
        </PanelFrame>
      </PanelConfigProvider>
    )
  }

  const grid = (
    <DashboardGrid ref={gridRef}>
      {layout.panels.map((entry, index) => {
        const panel = PANELS[entry.panel]

        return controls ? (
          <SortableGridItem
            key={entry.panel}
            id={entry.panel}
            span={entry.span}
            gridRef={gridRef}
            onResize={(span) => controls.resize(index, span)}
            resizeLabel={`Drag to resize — ${panel.title}`}
            dragHandle={({ attributes, listeners }) => (
              <DragHandle attributes={attributes} listeners={listeners} label={`Drag to reorder — ${panel.title}`} />
            )}
          >
            {(dragHandle) => renderPanel(entry, index, dragHandle, true)}
          </SortableGridItem>
        ) : (
          <GridItem key={entry.panel} span={entry.span}>
            {renderPanel(entry, index, null, false)}
          </GridItem>
        )
      })}
    </DashboardGrid>
  )

  if (!controls) {
    return <PanelPatientProvider uid={uid}>{grid}</PanelPatientProvider>
  }

  return (
    <PanelPatientProvider uid={uid}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          {grid}
        </SortableContext>

        <DragOverlay>
          {draggingEntry && (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                cursor: 'grabbing',
                filter: 'drop-shadow(0 12px 28px rgba(7, 26, 19, 0.28))',
              }}
            >
              {renderPanel(draggingEntry.entry, draggingEntry.index, null, false)}
            </Box>
          )}
        </DragOverlay>
      </DndContext>
    </PanelPatientProvider>
  )
}

export default DashboardRenderer
